<template>
  <div class="moneybird-manage">
    <div v-if="loading" class="moneybird-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <div
        class="moneybird-manage__banner"
        :class="isActive ? 'moneybird-manage__banner--success' : 'moneybird-manage__banner--info'"
      >
        <BaseIcon :name="isActive ? 'check-circle' : 'information-circle'" size="14" />
        <span>
          {{
            isActive ? $t('moneybird.status.active_message') : $t('moneybird.status.empty_message')
          }}
        </span>
      </div>

      <p v-if="status.administration_name" class="moneybird-manage__company">
        {{ $t('moneybird.settings.administration') }}:
        <strong>{{ status.administration_name }}</strong>
      </p>

      <div class="moneybird-manage__fields">
        <TextInput
          id="moneybird-api-token"
          type="password"
          :label="$t('moneybird.settings.api_token')"
          :info="$t('moneybird.settings.api_token_info')"
          v-model="form.api_token"
          :error="errors.api_token"
          :placeholder="
            status.has_api_token
              ? $t('moneybird.settings.api_token_placeholder_existing')
              : '••••••••'
          "
        />

        <div v-if="administrations.length > 1" class="moneybird-manage__field">
          <SelectInput
            id="moneybird-administration"
            :label="$t('moneybird.settings.administration')"
            v-model="form.administration_id"
            :options="administrationOptions"
            :error="errors.administration_id"
          />
          <p class="moneybird-manage__hint">
            {{ $t('moneybird.settings.administration_info') }}
          </p>
        </div>

        <template v-if="isConnected">
          <SelectInput
            id="moneybird-tax-high"
            :label="$t('moneybird.settings.tax_rate_high')"
            v-model="form.tax_rate_high"
            :options="taxRateOptions"
            :disabled="taxRateOptions.length <= 1"
          />

          <SelectInput
            id="moneybird-tax-low"
            :label="$t('moneybird.settings.tax_rate_low')"
            v-model="form.tax_rate_low"
            :options="taxRateOptions"
            :disabled="taxRateOptions.length <= 1"
          />

          <SelectInput
            id="moneybird-tax-zero"
            :label="$t('moneybird.settings.tax_rate_zero')"
            v-model="form.tax_rate_zero"
            :options="taxRateOptions"
            :disabled="taxRateOptions.length <= 1"
          />

          <div class="moneybird-manage__field">
            <SelectInput
              id="moneybird-tax-reverse"
              :label="$t('moneybird.settings.tax_rate_reverse_charge')"
              v-model="form.tax_rate_reverse_charge"
              :options="taxRateOptions"
              :disabled="taxRateOptions.length <= 1"
            />
            <p class="moneybird-manage__hint">
              {{ $t('moneybird.settings.tax_rate_reverse_charge_info') }}
            </p>
          </div>

          <div class="moneybird-manage__field">
            <SelectInput
              id="moneybird-ledger"
              :label="$t('moneybird.settings.ledger_account')"
              v-model="form.ledger_account_id"
              :options="ledgerOptions"
              :disabled="ledgerOptions.length <= 1"
            />
            <p class="moneybird-manage__hint">
              {{ $t('moneybird.settings.ledger_account_info') }}
            </p>
          </div>

          <div class="moneybird-manage__toggle">
            <ToggleInput
              id="moneybird-is-active"
              :label="$t('moneybird.settings.is_active')"
              v-model="form.is_active"
            />
            <p class="moneybird-manage__toggle-hint">
              {{ $t('moneybird.settings.is_active_info') }}
            </p>
          </div>

          <div
            class="moneybird-manage__webhook"
            :class="
              status.webhook_registered
                ? 'moneybird-manage__webhook--ok'
                : 'moneybird-manage__webhook--warn'
            "
          >
            <BaseIcon
              :name="status.webhook_registered ? 'check-circle' : 'exclamation-triangle'"
              size="14"
            />
            <span class="moneybird-manage__webhook-text">
              {{
                status.webhook_registered
                  ? $t('moneybird.settings.webhook_ok')
                  : $t('moneybird.settings.webhook_missing')
              }}
              <template v-if="!status.webhook_registered && status.webhook_error">
                <br />
                <small>{{ status.webhook_error }}</small>
              </template>
            </span>
            <Btn
              v-if="!status.webhook_registered"
              variant="outlined"
              size="sm"
              :loading="registeringWebhook"
              @click="handleRegisterWebhook"
            >
              {{ $t('moneybird.actions.register_webhook') }}
            </Btn>
          </div>
        </template>
      </div>

      <p class="moneybird-manage__help">
        {{ $t('moneybird.settings.help') }}
      </p>

      <div class="moneybird-manage__actions">
        <Btn v-if="isConnected" variant="danger" :loading="disconnecting" @click="handleDisconnect">
          {{ $t('moneybird.actions.disconnect') }}
        </Btn>
        <div v-else />

        <Btn variant="primary" :loading="saving" @click="handleSave">
          {{ isConnected ? $t('moneybird.actions.save') : $t('moneybird.actions.connect') }}
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  import moneybirdService from '@/services/moneybirdService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'MoneybirdManageModal',

    components: {
      Btn,
      BaseIcon,
      LoadingSpinner,
      TextInput,
      SelectInput,
      ToggleInput,
    },

    props: {
      modalId: { type: [String, Number], default: null },
      onUpdated: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const saving = ref(false);
      const disconnecting = ref(false);
      const registeringWebhook = ref(false);
      const status = ref({
        connected: false,
        platform_configured: true,
        has_api_token: false,
        webhook_registered: false,
        webhook_error: null,
      });
      const administrations = ref([]);
      const taxRateOptions = ref([]);
      const ledgerOptions = ref([]);
      const errors = reactive({ api_token: '', administration_id: '' });

      const form = reactive({
        api_token: '',
        administration_id: '',
        tax_rate_high: '',
        tax_rate_low: '',
        tax_rate_zero: '',
        tax_rate_reverse_charge: '',
        ledger_account_id: '',
        is_active: true,
      });

      const isConnected = computed(() => !!status.value.connected);
      const isActive = computed(() => isConnected.value && form.is_active);

      const administrationOptions = computed(() => [
        { value: '', label: $t('moneybird.settings.administration_empty') },
        ...administrations.value.map((a) => ({
          value: a.id,
          label: a.name || a.id,
        })),
      ]);

      const populateFromStatus = (s) => {
        status.value = s || {};
        form.tax_rate_high = s?.tax_rate_high || '';
        form.tax_rate_low = s?.tax_rate_low || '';
        form.tax_rate_zero = s?.tax_rate_zero || '';
        form.tax_rate_reverse_charge = s?.tax_rate_reverse_charge || '';
        form.ledger_account_id = s?.ledger_account_id || '';
        form.is_active = s?.is_active !== false;
      };

      const mapTaxRateOptions = (list) => {
        const empty = { value: '', label: $t('moneybird.settings.option_empty') };
        const mapped = (list || []).map((t) => ({
          value: t.id,
          label: `${t.name} (${t.percentage}%)`,
        }));
        return [empty, ...mapped];
      };

      const mapLedgerOptions = (list) => {
        const empty = { value: '', label: $t('moneybird.settings.option_empty') };
        const mapped = (list || []).map((l) => ({
          value: l.id,
          label: [l.account_id, l.name].filter(Boolean).join(' — '),
        }));
        return [empty, ...mapped];
      };

      const loadLookups = async () => {
        const [taxRes, ledgerRes] = await Promise.allSettled([
          moneybirdService.listTaxRates(),
          moneybirdService.listLedgerAccounts(),
        ]);
        taxRateOptions.value = mapTaxRateOptions(
          taxRes.status === 'fulfilled' ? taxRes.value?.tax_rates : []
        );
        ledgerOptions.value = mapLedgerOptions(
          ledgerRes.status === 'fulfilled' ? ledgerRes.value?.ledger_accounts : []
        );
      };

      const validate = () => {
        errors.api_token = '';
        errors.administration_id = '';
        if (!isConnected.value && !form.api_token?.trim()) {
          errors.api_token = $t('moneybird.settings.api_token_required');
          return false;
        }
        if (administrations.value.length > 1 && !form.administration_id) {
          errors.administration_id = $t('moneybird.settings.administration_required');
          return false;
        }
        return true;
      };

      const handleSave = async () => {
        if (!validate()) return;

        saving.value = true;
        try {
          const settingsPayload = {
            is_active: form.is_active,
            tax_rate_high: form.tax_rate_high || null,
            tax_rate_low: form.tax_rate_low || null,
            tax_rate_zero: form.tax_rate_zero || null,
            tax_rate_reverse_charge: form.tax_rate_reverse_charge || null,
            ledger_account_id: form.ledger_account_id || null,
          };

          let result;
          if (!isConnected.value || form.api_token.trim()) {
            const connectPayload = {
              api_token: form.api_token.trim(),
              administration_id: form.administration_id || null,
              is_active: form.is_active,
            };
            // Only send explicit tax/ledger overrides on reconnect; on a first
            // connect the backend auto-detects them.
            if (isConnected.value) Object.assign(connectPayload, settingsPayload);
            result = await moneybirdService.connect(connectPayload);
          } else {
            result = await moneybirdService.update(settingsPayload);
          }

          administrations.value = [];
          form.administration_id = '';
          populateFromStatus(result?.status || (await moneybirdService.getStatus()));
          form.api_token = '';
          await loadLookups();

          toastStore.addToast({
            type: 'success',
            message: $t('moneybird.save.success'),
          });

          if (typeof props.onUpdated === 'function') {
            await props.onUpdated();
          }
        } catch (error) {
          const data = error?.response?.data;
          if (
            data?.code === 'MONEYBIRD_ADMINISTRATION_REQUIRED' &&
            Array.isArray(data.administrations)
          ) {
            administrations.value = data.administrations;
            toastStore.addToast({
              type: 'info',
              message: $t('moneybird.settings.administration_pick'),
              duration: 5000,
            });
            return;
          }
          const msg = data?.error || error.message || $t('moneybird.save.error');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          saving.value = false;
        }
      };

      const handleRegisterWebhook = async () => {
        registeringWebhook.value = true;
        try {
          const s = await moneybirdService.registerWebhook();
          populateFromStatus(s);
          toastStore.addToast({
            type: s?.webhook_registered ? 'success' : 'error',
            message: s?.webhook_registered
              ? $t('moneybird.webhook.success')
              : s?.webhook_error || $t('moneybird.webhook.error'),
          });
        } catch (error) {
          const msg =
            error?.response?.data?.error || error.message || $t('moneybird.webhook.error');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          registeringWebhook.value = false;
        }
      };

      const handleDisconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: $t('moneybird.disconnect.title'),
          description: $t('moneybird.disconnect.description'),
          status: 'danger',
          submitText: $t('moneybird.disconnect.confirm'),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await moneybirdService.disconnect();
              uiStore.closeModal(confirmId);
              if (props.modalId) uiStore.closeModal(props.modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('moneybird.disconnect.success'),
              });
              if (typeof props.onUpdated === 'function') {
                await props.onUpdated();
              }
            } catch (error) {
              uiStore.closeModal(confirmId);
              const msg =
                error?.response?.data?.error || error.message || $t('moneybird.disconnect.error');
              toastStore.addToast({ type: 'error', message: msg });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        try {
          const s = await moneybirdService.getStatus();
          populateFromStatus(s);
          if (s?.connected) {
            await loadLookups();
          } else {
            taxRateOptions.value = mapTaxRateOptions([]);
            ledgerOptions.value = mapLedgerOptions([]);
          }
        } catch (error) {
          console.error('Failed to load Moneybird status:', error);
        } finally {
          loading.value = false;
        }
      });

      return {
        loading,
        saving,
        disconnecting,
        registeringWebhook,
        status,
        form,
        errors,
        administrations,
        administrationOptions,
        taxRateOptions,
        ledgerOptions,
        isConnected,
        isActive,
        handleSave,
        handleRegisterWebhook,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .moneybird-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.45;

      &--info {
        background: var(--slate-50);
        color: var(--slate-700);
      }

      &--success {
        background: var(--green-50);
        color: var(--green-800);
      }
    }

    &__company {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__field,
    &__toggle {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__hint,
    &__toggle-hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__webhook {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      line-height: 1.4;

      &--ok {
        background: var(--green-50);
        color: var(--green-800);
      }

      &--warn {
        background: var(--amber-50);
        color: var(--amber-800);
      }

      small {
        color: inherit;
        opacity: 0.8;
      }
    }

    &__webhook-text {
      flex: 1;
      min-width: 0;
    }

    &__help {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.45;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      padding-top: 0.25rem;
    }
  }
</style>
